import React, { useState } from 'react'
import { Icon, Menu, MenuItem } from '@blueprintjs/core'
import { IGridTableColumn, Skeleton, IconButton, Popover, Link, Flex } from '@ui'
import { IBooking, ISettings } from '@api'
import css from './bookingsModal.module.scss'
import cn from 'classnames'
import CheckInButton from 'components/CheckInButton/CheckInButton'
import { DateUtil, TIME, isMultiDayBooking, isRecurringBooking, shareBookingSpace } from '@utils'
import { get } from 'lodash'
import { Tooltip2 } from '@blueprintjs/popover2'
import BookingCalendarPopup from 'components/BookingCalendarPopup/BookingCalendarPopup'
import { useFloor } from '@providers/mapProvider'

type GetBookingColumns = (params: {
  cancelBooking: (bookingId: string | undefined) => void
  isCurrentBookings: boolean
  spaceLocate?: (locate: { spaceId: string; floorId: string }) => void
  closeModal: () => void
  settings: ISettings
  editBooking: (booking: IBooking) => void
  setIsBookDeskPage?: (floorId: string) => void
  isBookDeskPage: boolean
}) => IGridTableColumn<IBooking>[]

const createBookingColumns: GetBookingColumns = ({
  cancelBooking,
  settings,
  isCurrentBookings,
  spaceLocate,
  closeModal,
  editBooking,
  setIsBookDeskPage,
  isBookDeskPage,
}) => {
  const cols: IGridTableColumn<IBooking>[] = [
    {
      label: 'Date',
      sort: 'startDate',
      colSpan: 2,
      Cell: ({ skeleton, cell }) => {
        if (!cell || !cell.data) return null
        const timezone = cell.data.calendar
          ? get(cell, 'data.calendar.space.floor.building.timezone')
          : cell?.data?.space?.floor?.building?.timezone ?? ''
        const start = DateUtil.utc(
          cell.data.calendar
            ? (get(cell, 'data.start') as unknown as string)
            : cell?.data?.startDate,
          timezone
        )
        const end = DateUtil.utc(
          cell.data.calendar ? (get(cell, 'data.end') as unknown as string) : cell?.data?.endDate,
          timezone
        )
        const isMultiDay = isMultiDayBooking(cell?.data)
        const isRecurrence = isRecurringBooking(cell?.data)
        const formatDateString = 'MMMM dd, yyyy'
        return skeleton ? (
          <Skeleton withButton />
        ) : (
          <div data-test="bookings_date" className="flex flex-row">
            <div className="font-light">
              <Tooltip2
                position="right"
                content={
                  <div>
                    {start.isSame(end, TIME.DAYS)
                      ? start.format(formatDateString)
                      : `${start.format(formatDateString)} - ${end.format(formatDateString)}`}
                  </div>
                }>
                {start.format(formatDateString)}
                {(isMultiDay || isRecurrence) && (
                  <div
                    style={{
                      background: isMultiDay ? '#e6eeff' : '#eaf8d1',
                      padding: '6px',
                      borderRadius: '12px',
                    }}>
                    {isMultiDay ? 'Multi-Day' : 'Recurring'}
                  </div>
                )}
              </Tooltip2>
            </div>
          </div>
        )
      },
    },
    {
      label: 'Time',
      colSpan: 2,
      Cell: ({ skeleton, cell }) => {
        if (!cell || !cell.data) return null
        const timezone = cell.data.calendar
          ? get(cell, 'data.calendar.space.floor.building.timezone')
          : cell?.data?.space?.floor?.building?.timezone ?? ''
        const start = DateUtil.utc(
          cell.data.calendar
            ? (get(cell, 'data.start') as unknown as string)
            : cell?.data?.startDate,
          timezone
        )
        const end = DateUtil.utc(
          cell.data.calendar ? (get(cell, 'data.end') as unknown as string) : cell?.data?.endDate,
          timezone
        )
        const zoneTip = `${timezone}`
        return skeleton ? (
          <Skeleton withButton />
        ) : (
          <div data-test="bookings_date" className="flex flex-row">
            <div className="font-light">
              {/* <Tooltip2 content={zoneTip}>
                {start.format('h:mm a')} to {end.format('h:mm a')}
              </Tooltip2> */}
              {start.format('h:mm a')} to {end.format('h:mm a')}
            </div>
          </div>
        )
      },
    },
    {
      label: 'Address',
      colSpan: 3,
      Cell: ({ skeleton, cell }) =>
        skeleton ? (
          <Skeleton />
        ) : (
          <div data-test="bookings_location" className="flex flex-col">
            <div className="font-light">
              {get(cell, 'data.calendar')
                ? get(cell, 'data.calendar.space.floor.building.address')
                : cell?.data?.space?.floor?.building?.address}{' '}
              {get(cell, 'data.calendar')
                ? get(cell, 'data.calendar.space.floor.building.city')
                : cell?.data?.space?.floor?.building?.city}
            </div>
          </div>
        ),
    },
    {
      label: 'Note',
      sort: 'note',
      colSpan: 1,
      Cell: ({ skeleton, cell }) =>
        skeleton ? (
          <Skeleton />
        ) : (
          <div className="font-light">
            {cell?.data.calendar ? get(cell, 'data.calendar.space.note') : cell?.data?.note}
          </div>
        ),
    },
    {
      label: 'Space',
      sort: 'space.externalId',
      colSpan: 1,
      Cell: ({ skeleton, cell }) =>
        skeleton ? (
          <Skeleton />
        ) : (
          <Link
            onClick={() => {
              const spaceId = cell?.data.calendar
                ? get(cell?.data, 'calendar.space.id', '')
                : get(cell?.data, 'space.id', '')
              const floorId = cell?.data.calendar
                ? get(cell?.data, 'calendar.space.floor.id', '')
                : get(cell?.data, 'space.floor.id', '')
              spaceLocate?.({ floorId, spaceId })
              if (!isBookDeskPage) {
                setIsBookDeskPage?.(floorId)
              }
              closeModal()
            }}>
            {cell?.data.calendar
              ? get(cell, 'data.calendar.space.externalId')
              : cell?.data?.space?.externalId}
          </Link>
        ),
    },
  ]
  if (isCurrentBookings) {
    cols.push({
      label: 'Actions',
      colSpan: 2,
      alignRight: true,
      cellStyle: {
        paddingRight: 20,
      },
      Cell: ({ skeleton, cell }) => {
        const canCancel =
          settings.bookingCancellationEnabled &&
          (cell?.data.checkedIn ? settings.bookingCheckInCancelEnabled : true)
        const cancelMsg = canCancel
          ? undefined
          : 'You don’t have permission to cancel this booking. Please contact the administrator to make this change'
        const editMsg = cell?.data.checkedIn ? 'Cannot edit booking if checked in' : undefined
        return skeleton ? (
          <Skeleton />
        ) : (
          <Flex align="center" justify="space-between">
            <CheckInButton booking={cell?.data} />
            <Popover
              popoverClassName={css.actions_wrapper}
              placement="bottom-end"
              content={
                <Menu className={css.menu_color}>
                  <MenuItem
                    text={
                      <Tooltip2 className={cn(css.tooltip)} content={editMsg}>
                        Edit
                      </Tooltip2>
                    }
                    icon={<Icon className={css.menu_color} icon="edit" />}
                    onClick={() => editBooking(cell?.data as IBooking)}
                  />
                  <MenuItem
                    text="Share"
                    icon={<Icon className={css.menu_color} icon="share" />}
                    onClick={() => shareBookingSpace(cell?.data as IBooking)}
                  />
                  <MenuItem
                    text={
                      <Tooltip2 className={cn(css.tooltip)} content={cancelMsg}>
                        Delete
                      </Tooltip2>
                    }
                    icon={<Icon className={css.menu_color} icon="trash" />}
                    onClick={() =>
                      canCancel &&
                      cancelBooking(
                        isRecurringBooking(cell?.data as IBooking)
                          ? cell?.data.bookingSchedule?.id
                          : cell?.data.id
                      )
                    }
                  />
                </Menu>
              }>
              <IconButton className={css.ellipsis_button} round={false} iconName="ellipsis-v" />
            </Popover>
          </Flex>
        )
      },
    })
  }
  if (!settings.bookingNotesEnabled) cols.splice(3, 1)
  return cols
}

export default createBookingColumns
